import { useEffect, useRef, useState } from 'react';
import { useWorkflow } from '../../workflow/hooks';
import DOMPurify, { type Config } from 'dompurify';
import type { PromoConfig } from '../../graphql/query/getRetailerSettings';
import { pdxEvent } from '../../utils/noflake';

const PROMO_TRACKERS = {
  CLICK: 'data-track-click',
} as Record<string, string>;

const DEFAULT_PURIFY_CONFIG: Config = {
  ADD_ATTR: [
    'target',
    'class',
    'style',
    'type',
    'href',
    ...Object.values(PROMO_TRACKERS),
  ],
  ADD_TAGS: ['style'],
  ALLOWED_TAGS: ['div', 'span', 'p', 'a', 'img', 'button', 'h1', 'h2', 'h3'],
  ALLOW_DATA_ATTR: false,
  ALLOW_ARIA_ATTR: true,
  ALLOW_UNKNOWN_PROTOCOLS: false,
  FORCE_BODY: true,
  FORBID_TAGS: ['script', 'iframe', 'object', 'embed'],
  FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover'],
  SAFE_FOR_TEMPLATES: true,
};

type Location = 'complete' | 'claimDetails';
type Area = 'pageTop' | 'pageBottom';

type PromoProps = {
  id: `${Location}.${Area}`;
};

/**
 * Quin config example:
 *
 * Click events can be tracked by adding a `data-track-click` attribute to the promo content.
 *
 * {
 *   "settings_json": {
 *     "promos": [
 *       {
 *         "promoId": "promo1",
 *         "ids": ["complete.pageTop"],
 *         "content": "<div>Promo Content for Complete Page Top</div>"
 *       },
 *       {
 *         "promoId": "promo2",
 *         "ids": ["complete.pageBottom", "claimDetails.pageBottom"],
 *         "content": "<a href='example' data-track-click='june-promo'>Promo Content for Claim Details & Complete Page Bottom</a>"
 *       }
 *     ]
 *   }
 * }
 */

export const Promo = ({ id }: PromoProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [shadowRoot, setShadowRoot] = useState<ShadowRoot | null>(null);
  const { retailerSettings } = useWorkflow();

  const matchingConfig = retailerSettings?.promos?.find((promo) => {
    const typedPromo = promo as PromoConfig;
    return typedPromo?.ids?.includes(id);
  });

  // Track clicked elements to prevent excessive clicks
  const clickedElements = useRef<Map<HTMLElement, number>>(new Map());
  const isNavigating = useRef(false);

  // Track if promo has been viewed to prevent duplicate view events
  const hasBeenViewed = useRef(false);
  const intersectionObserver = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    if (containerRef.current && !shadowRoot) {
      const shadow = containerRef.current.attachShadow({ mode: 'open' });
      setShadowRoot(shadow);
    }
  }, [shadowRoot]);

  // Set up Intersection Observer to track when promo becomes visible
  useEffect(() => {
    if (!containerRef.current || hasBeenViewed.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasBeenViewed.current) {
            hasBeenViewed.current = true;

            if (matchingConfig) {
              pdxEvent({
                event_category: 'user_action',
                event_type: 'view',
                event_value: matchingConfig.promoId ?? null,
                event_details: id,
              });
            }

            // Disconnect observer since we only need to track the first view
            observer.disconnect();
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -10% 0px',
      },
    );

    observer.observe(containerRef.current);
    intersectionObserver.current = observer;

    return () => {
      observer.disconnect();
    };
  }, [retailerSettings, id, matchingConfig]);

  useEffect(() => {
    if (shadowRoot) {
      if (matchingConfig) {
        shadowRoot.innerHTML = DOMPurify.sanitize(
          matchingConfig.content,
          DEFAULT_PURIFY_CONFIG,
        );

        const trackClickElements = shadowRoot.querySelectorAll(
          `[${PROMO_TRACKERS.CLICK}]`,
        );

        trackClickElements.forEach((element) => {
          if (!PROMO_TRACKERS.CLICK) return;
          const trackValue = element.getAttribute(PROMO_TRACKERS.CLICK);
          if (!trackValue) return;

          element.addEventListener('click', async (event) => {
            event.preventDefault();

            const target = event.currentTarget as HTMLElement;

            // Check if element was recently clicked to prevent excessive
            // clicks while noflake is resolving
            const lastClickTime = clickedElements.current.get(target);
            const now = Date.now();
            if (
              (lastClickTime && now - lastClickTime < 2000) ||
              isNavigating.current
            ) {
              return;
            }
            clickedElements.current.set(target, now);
            isNavigating.current = true;

            const href = (target as HTMLAnchorElement).href;
            const isNewTab = (target as HTMLAnchorElement).target === '_blank';

            const continueNavigation = () => {
              if (href) {
                if (isNewTab) {
                  window.open(href, '_blank');
                } else {
                  window.location.href = href;
                }
              }

              // Reset navigation flag after timeout to allow clicks again
              setTimeout(() => {
                isNavigating.current = false;
              }, 5000);
            };

            pdxEvent({
              event_category: 'user_action',
              event_type: 'click',
              event_value: matchingConfig.promoId ?? null,
              event_details: trackValue,
            })
              .then(continueNavigation)
              .catch(continueNavigation);
          });
        });
      }
    }
  }, [shadowRoot, retailerSettings, id, matchingConfig]);

  return <div ref={containerRef} />;
};
